import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Button, Badge, Form, InputGroup, Card, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import CreateRecommendationModal from "../components/CreateRecommendationModal";
import EditRecommendationModal from "../components/EditRecommendationModal";

import { FaPlus, FaEdit, FaToggleOn, FaToggleOff, FaSearch, FaFileAlt } from "react-icons/fa";

import {
    whoami,
    getAllRecommendations,
    getAllAssessments,
    getRecommendationsByAssessmentId,
    activateRecommendation,
    deactivateRecommendation,
    getCountries,
} from "../services/APIClient";

import "../components/RecommendationsAdmin.css";

function RecommendationsAdmin() {
    const navigate = useNavigate();
    const [teacherid, setTeacherid] = useState("");
    const [name, setName] = useState("");
    const [recommendations, setRecommendations] = useState([]);
    const [filteredRecommendations, setFilteredRecommendations] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [selectedAssessment, setSelectedAssessment] = useState("all");
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState("all");
    const [assessmentTaskGroups, setAssessmentTaskGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterActive, setFilterActive] = useState("all"); // all, active, inactive
    const [isPortrait, setIsPortrait] = useState(window.matchMedia("(orientation: portrait)").matches);

    const [showModal, setShowModal] = useState({
        create: false,
        edit: false,
    });

    const [selectedRecommendation, setSelectedRecommendation] = useState(null);
    const [prefillTaskId, setPrefillTaskId] = useState(null);

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const response = await whoami();
                const stakeholder = response.data.stakeholder;
                setTeacherid(stakeholder.id);
                setName(stakeholder.name);
            } catch (error) {
                console.error("Error fetching user info:", error);
                setError("Failed to load user information");
            }
        };

        fetchUserInfo();
    }, []);

    useEffect(() => {
        fetchAssessments();
        fetchCountries();
    }, []);

    useEffect(() => {
        fetchRecommendations();
    }, [selectedAssessment]);

    useEffect(() => {
        filterAndSearchRecommendations();
    }, [recommendations, searchTerm, filterActive]);

    useEffect(() => {
        const handleOrientationChange = () => {
            setIsPortrait(window.innerWidth < window.innerHeight);
        };

        window.addEventListener("resize", handleOrientationChange);
        return () => {
            window.removeEventListener("resize", handleOrientationChange);
        };
    }, []);

    const fetchAssessments = async () => {
        setIsLoadingAssessments(true);
        try {
            const data = await getAllAssessments();
            setAssessments(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching assessments:", error);
        } finally {
            setIsLoadingAssessments(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const data = await getCountries();
            setCountries(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };

    const getMissingTasks = () => {
        return assessmentTaskGroups.filter(group => group.recommendations.length === 0);
    };

    const PERIOD_ORDER = ["baseline", "midpoint", "end of year"];

    const getGradeRank = (assessment) => {
        if (assessment.grade_level1) return 1;
        if (assessment.grade_level2) return 2;
        if (assessment.grade_level3) return 3;
        if (assessment.grade_level4) return 4;
        return 99;
    };

    const getPeriodRank = (name) => {
        const lower = name.toLowerCase();
        const idx = PERIOD_ORDER.findIndex(p => lower.includes(p));
        return idx === -1 ? 99 : idx;
    };

    const getFilteredAssessments = () => {
        return assessments
            .filter(a => a.standard === true)
            .filter(a => selectedCountry === "all" || a.countryid === selectedCountry)
            .sort((a, b) => {
                const gradeDiff = getGradeRank(a) - getGradeRank(b);
                if (gradeDiff !== 0) return gradeDiff;
                return getPeriodRank(a.name) - getPeriodRank(b.name);
            });
    };

    const fetchRecommendations = async () => {
        setIsLoading(true);
        setError(null);
        try {
            let data;
            if (selectedAssessment && selectedAssessment !== "all") {
                const groups = await getRecommendationsByAssessmentId(selectedAssessment);
                setAssessmentTaskGroups(Array.isArray(groups) ? groups : []);
                data = groups.flatMap(group =>
                    group.recommendations.map(rec => ({
                        ...rec,
                        task: { name: group.task_name }
                    }))
                );
            } else {
                setAssessmentTaskGroups([]);
                data = await getAllRecommendations();
            }

            setRecommendations(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching recommendations:", error);
            setError("Failed to load recommendations");
            setRecommendations([]);
        } finally {
            setIsLoading(false);
        }
    };

    const filterAndSearchRecommendations = () => {
        let filtered = [...recommendations];

        // Filter by active status
        if (filterActive === "active") {
            filtered = filtered.filter(rec => rec.is_active);
        } else if (filterActive === "inactive") {
            filtered = filtered.filter(rec => !rec.is_active);
        }

        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(rec =>
                rec.name.toLowerCase().includes(searchLower) ||
                rec.url.toLowerCase().includes(searchLower) ||
                (rec.description && rec.description.toLowerCase().includes(searchLower)) ||
                (rec.task?.name && rec.task.name.toLowerCase().includes(searchLower))
            );
        }

        setFilteredRecommendations(filtered);
    };

    const handleOpenModal = (modalName, recommendation = null, taskId = null) => {
        setSelectedRecommendation(recommendation);
        setPrefillTaskId(taskId);
        setShowModal({ ...showModal, [modalName]: true });
    };

    const handleCloseModal = (modalName) => {
        setShowModal({ ...showModal, [modalName]: false });
        setSelectedRecommendation(null);
        setPrefillTaskId(null);
    };

    const handleCreateSuccess = () => {
        fetchRecommendations();
        handleCloseModal("create");
    };

    const handleEditSuccess = () => {
        fetchRecommendations();
        handleCloseModal("edit");
    };

    const handleToggleActive = async (recommendation) => {
        try {
            if (recommendation.is_active) {
                await deactivateRecommendation(recommendation.id, name);
            } else {
                await activateRecommendation(recommendation.id, name);
            }
            fetchRecommendations();
        } catch (error) {
            console.error("Error toggling recommendation status:", error);
            setError("Failed to update recommendation status");
        }
    };

    return (
        <div>
            <Container fluid className="recommendations-admin-container">
                <HeadingBanner
                    name={name}
                    isLoggedIn={true}
                    cloud={true}
                    teacherid={teacherid}
                />

                <Row className="recommendations-admin-row">
                    <LeftPanel />
                    <Col sm={{ span: 10, offset: 2 }} className="recommendations-admin-content">

                        <div className="recommendations-admin-header">
                            <div>
                                <h2 className="recommendations-admin-page-title">Recommendations Management</h2>
                                <p className="recommendations-admin-page-subtitle">
                                    Create, edit, and manage recommendation PDFs for all tasks
                                </p>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => handleOpenModal("create")}
                                className="create-recommendation-btn"
                            >
                                <FaPlus className="me-2" />
                                Create Recommendation
                            </Button>
                        </div>

                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <Card className="filters-card mb-4">
                            <Card.Body>
                                <Row>
                                    <Col md={3}>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaSearch />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by name, URL, or description..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </InputGroup>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select
                                            value={selectedCountry}
                                            onChange={(e) => {
                                                setSelectedCountry(e.target.value);
                                                setSelectedAssessment("all");
                                            }}
                                        >
                                            <option value="all">All Countries</option>
                                            {countries.map((country) => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select
                                            value={selectedAssessment}
                                            onChange={(e) => setSelectedAssessment(e.target.value)}
                                            disabled={isLoadingAssessments}
                                        >
                                            <option value="all">All Assessments</option>
                                            {getFilteredAssessments().map((assessment) => (
                                                <option key={assessment.id} value={assessment.id}>
                                                    {assessment.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select
                                            value={filterActive}
                                            onChange={(e) => setFilterActive(e.target.value)}
                                        >
                                            <option value="all">All Recommendations</option>
                                            <option value="active">Active Only</option>
                                            <option value="inactive">Inactive Only</option>
                                        </Form.Select>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {selectedAssessment !== "all" && !isLoading && assessmentTaskGroups.length > 0 && (() => {
                            const missing = getMissingTasks();
                            return missing.length > 0 ? (
                                <Alert variant="warning" className="mb-4">
                                    <Alert.Heading className="h6">Tasks missing recommendations ({missing.length})</Alert.Heading>
                                    <ul className="mb-0 mt-2">
                                        {missing.map(group => (
                                            <li key={group.taskid}>
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-start"
                                                    style={{ color: "inherit", textDecoration: "underline dotted" }}
                                                    onClick={() => handleOpenModal("create", null, group.taskid)}
                                                >
                                                    {group.task_name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </Alert>
                            ) : (
                                <Alert variant="success" className="mb-4">
                                    All tasks for this assessment have recommendations.
                                </Alert>
                            );
                        })()}

                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading recommendations...</p>
                            </div>
                        ) : (
                            <Card>
                                <Card.Body className="p-0">
                                    <Table responsive hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Status</th>
                                                <th>Name</th>
                                                <th>Task</th>
                                                <th>URL</th>
                                                <th>Country</th>
                                                <th>Last Updated</th>
                                                <th>Updated By</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRecommendations.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-5">
                                                        <FaFileAlt size={48} className="text-muted mb-3" />
                                                        <p className="text-muted">
                                                            {searchTerm || filterActive !== "all" || selectedAssessment !== "all"
                                                                ? "No recommendations found matching your filters"
                                                                : "No recommendations available. Create your first one!"}
                                                        </p>
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredRecommendations.map((rec) => {
                                                    const isGrouped = !!rec.parentTaskId;
                                                    
                                                    return (
                                                        <tr key={rec.id}>
                                                            <td>
                                                                {rec.is_active ? (
                                                                    <Badge bg="success">Active</Badge>
                                                                ) : (
                                                                    <Badge bg="secondary">Inactive</Badge>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <strong>{rec.name}</strong>
                                                                {rec.description && (
                                                                    <div className="text-muted small">{rec.description}</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {rec.task?.name || "N/A"}
                                                                {isGrouped && (
                                                                    <Badge bg="info" className="ms-2" title="Group recommendation">
                                                                        Group
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <a href={rec.url} target="_blank" rel="noopener noreferrer" className="text-truncate d-inline-block" style={{ maxWidth: "200px" }}>
                                                                    {rec.url}
                                                                </a>
                                                            </td>
                                                            <td>{rec.countryid || "All"}</td>
                                                            <td className="text-muted small">
                                                                {rec.updated_at
                                                                    ? new Date(rec.updated_at).toLocaleDateString('en-IE', {
                                                                        day: '2-digit',
                                                                        month: '2-digit',
                                                                        year: 'numeric'
                                                                    })
                                                                    : "N/A"}
                                                            </td>
                                                            <td className="text-muted small">{rec.updated_by || "N/A"}</td>
                                                            <td>
                                                                <div className="action-buttons">
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleOpenModal("edit", rec)}
                                                                        className="me-2"
                                                                        title={isGrouped ? "Edit group recommendation" : "Edit recommendation"}
                                                                    >
                                                                        <FaEdit />
                                                                    </Button>
                                                                    <Button
                                                                        variant={rec.is_active ? "outline-warning" : "outline-success"}
                                                                        size="sm"
                                                                        onClick={() => handleToggleActive(rec)}
                                                                        title={rec.is_active ? "Deactivate" : "Activate"}
                                                                    >
                                                                        {rec.is_active ? <FaToggleOn /> : <FaToggleOff />}
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}

                        <div className="recommendations-count mt-3 text-muted">
                            Showing {filteredRecommendations.length} of {recommendations.length} recommendations
                        </div>

                    </Col>
                </Row>
            </Container>

            {isPortrait && <OrientationPrompt />}

            <CreateRecommendationModal
                show={showModal.create}
                onHide={() => handleCloseModal("create")}
                onSuccess={handleCreateSuccess}
                userName={name}
                prefillTaskId={prefillTaskId}
            />

            <EditRecommendationModal
                show={showModal.edit}
                onHide={() => handleCloseModal("edit")}
                recommendation={selectedRecommendation}
                onSuccess={handleEditSuccess}
                userName={name}
            />
        </div>
    );
}

export default RecommendationsAdmin;